import { useState, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import Mammoth from "mammoth";
import * as XLSX from "xlsx";
import "./Preview1.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const Preview1 = ({ fileUrl, fileName, onClose }) => {
  const [docxContent, setDocxContent] = useState("");
  const [numPages, setNumPages] = useState(null);
  const [error, setError] = useState("");
  const [textContent, setTextContent] = useState("");
  const [excelData, setExcelData] = useState([]);
  const [docViewerUrl, setDocViewerUrl] = useState("");

  useEffect(() => {
    if (!fileUrl) return;

    const urlWithNoCache = `${fileUrl}?nocache=${Date.now()}`;

    if (fileUrl.endsWith(".docx")) {
      handleDocxPreview(urlWithNoCache);
    } else if (fileUrl.endsWith(".doc")) {
      handleDocPreview(urlWithNoCache);
    } else if (fileUrl.match(/\.(txt|csv|log|json)$/)) {
      handleTextPreview(urlWithNoCache);
    } else if (fileUrl.match(/\.(xlsx|xls)$/)) {
      handleXlsxPreview(urlWithNoCache);
    }
  }, [fileUrl]);

  const handleDocxPreview = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch file.");
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onload = async (event) => {
        const result = await Mammoth.convertToHtml({
          arrayBuffer: event.target.result,
        });
        if (!result.value.trim()) throw new Error("Empty DOCX content.");
        setDocxContent(result.value);
      };
      reader.onerror = () => setError("Error reading the DOCX file.");
      reader.readAsArrayBuffer(blob);
    } catch (err) {
      console.error("DOCX Error:", err);
      setError("Could not preview DOCX file.");
    }
  };

  const handleDocPreview = (url) => {
    const googleViewerUrl = `https://docs.google.com/gview?url=${url}&embedded=true`;
    setDocViewerUrl(googleViewerUrl);
  };

  const handleTextPreview = async (url) => {
    try {
      const response = await fetch(url);
      const text = await response.text();
      setTextContent(text);
    } catch (err) {
      console.error("Text file error:", err);
      setError("Could not load text file.");
    }
  };

  const handleXlsxPreview = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch the Excel file.");
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      const data = new Uint8Array(arrayBuffer);
      const workbook = XLSX.read(data, { type: "array" });

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      setExcelData(rows);
    } catch (err) {
      setError("Could not load the Excel file.");
      console.error(err);
    }
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <div className="modal-backdrop-pre">
      <div className="modal-review-pre">
        <div className="modal-content-review-pre">
          <h3 className="modal-title-pre">{fileName}</h3>
          <div className="preview-container-pre">
            {error ? (
              <p className="error-message">{error}</p>
            ) : fileUrl.endsWith(".pdf") ? (
              <Document
                file={fileUrl}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                onLoadError={() => setError("Unable to load PDF.")}
              >
                {Array.from(new Array(numPages), (el, index) => (
                  <Page key={index} pageNumber={index + 1} width={800} />
                ))}
              </Document>
            ) : fileUrl.endsWith(".docx") ? (
              <div
                className="docx-preview"
                dangerouslySetInnerHTML={{
                  __html: docxContent || "Loading DOCX...",
                }}
              />
            ) : fileUrl.endsWith(".doc") ? (
              <p>Doc files can't be reviewed</p>
            ) : fileUrl.match(/\.(jpg|jpeg|png|gif|bmp|svg)$/) ? (
              <img src={fileUrl} alt="Preview" className="image-preview" />
            ) : fileUrl.match(/\.(txt|csv|json|log)$/) ? (
              <pre className="text-preview">
                {textContent || "Loading text..."}
              </pre>
            ) : fileUrl.match(/\.(xlsx|xls)$/) ? (
              <div className="excel-preview">
                {excelData.length > 0 ? (
                  <table>
                    <tbody>
                      {excelData.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {row.map((cell, colIndex) => (
                            <td key={colIndex}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>Loading Excel...</p>
                )}
              </div>
            ) : (
              <p>Preview not available for this file type.</p>
            )}
          </div>
        </div>
        <div className="modal-footer-pre">
          <div>
            <button className="download-btn-pre">
              <a href={fileUrl} download>
                Download
              </a>
            </button>
          </div>
          <div>
            <button className="close-btn-pre" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Preview1;
